import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  MessageSquare, 
  Phone, 
  Mail,
  Calendar,
  DollarSign,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  CheckCircle2,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { useTranslation } from 'react-i18next';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  totalBookings: number;
  lastBooking: string;
  status: 'active' | 'inactive' | 'potential';
  bookedCreneaux: Creneau[];
}

const ClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [creneauToReject, setCreneauToReject] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');

  useEffect(() => {
    const loadClientsData = async () => {
      try {
        setIsLoading(true);
        const bookedCreneaux = await creneauxService.getAllMyBookings();
        
        // Group créneaux by trainee to create client records
        const clientMap = new Map<string, Client>();
        
        bookedCreneaux.forEach((creneau) => {
          if (creneau.trainee) {
            const traineeId = creneau.trainee.id.toString();
            
            if (!clientMap.has(traineeId)) {
              clientMap.set(traineeId, {
                id: traineeId,
                name: `${creneau.trainee.firstname} ${creneau.trainee.lastname}`,
                email: creneau.trainee.email || '',
                phone: creneau.trainee.phone || '',
                totalBookings: 0,
                lastBooking: creneau.creneau_date,
                status: 'active' as const,
                bookedCreneaux: []
              });
            }
            
            const client = clientMap.get(traineeId)!;
            client.totalBookings++;
            client.bookedCreneaux.push(creneau);
            
            // Update last booking date if this one is more recent
            if (new Date(creneau.creneau_date) > new Date(client.lastBooking)) {
              client.lastBooking = creneau.creneau_date;
            }
          }
        });
        
        setClients(Array.from(clientMap.values()));
      } catch (error) {
        setClients([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClientsData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadClientsData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (creneauId: number) => {
    try {
      setActionLoading(creneauId);
      setErrorMessage(null);
      setSuccessMessage(null);
      
      await creneauxService.approveBooking(creneauId);
      
      setSuccessMessage(t('consultant.clients.approved'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reload data
      const bookedCreneaux = await creneauxService.getAllMyBookings();
      const clientMap = new Map<string, Client>();
      
      bookedCreneaux.forEach((creneau) => {
        if (creneau.trainee) {
          const traineeId = creneau.trainee.id.toString();
          
          if (!clientMap.has(traineeId)) {
            clientMap.set(traineeId, {
              id: traineeId,
              name: `${creneau.trainee.firstname} ${creneau.trainee.lastname}`,
              email: creneau.trainee.email || '',
              phone: creneau.trainee.phone || '',
              totalBookings: 0,
              lastBooking: creneau.creneau_date,
              status: 'active' as const,
              bookedCreneaux: []
            });
          }
          
          const client = clientMap.get(traineeId)!;
          client.totalBookings++;
          client.bookedCreneaux.push(creneau);
          
          if (new Date(creneau.creneau_date) > new Date(client.lastBooking)) {
            client.lastBooking = creneau.creneau_date;
          }
        }
      });
      
      setClients(Array.from(clientMap.values()));
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || t('consultant.clients.approvalError'));
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClick = (creneauId: number) => {
    setCreneauToReject(creneauId);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setCreneauToReject(null);
    setRejectionReason('');
  };

  const handleConfirmReject = async () => {
    if (!creneauToReject) return;

    try {
      setActionLoading(creneauToReject);
      setErrorMessage(null);
      setSuccessMessage(null);
      setShowRejectModal(false);
      
      await creneauxService.rejectBooking(creneauToReject, rejectionReason || undefined);
      
      setSuccessMessage(t('consultant.clients.rejected'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reload data
      const bookedCreneaux = await creneauxService.getAllMyBookings();
      const clientMap = new Map<string, Client>();
      
      bookedCreneaux.forEach((creneau) => {
        if (creneau.trainee) {
          const traineeId = creneau.trainee.id.toString();
          
          if (!clientMap.has(traineeId)) {
            clientMap.set(traineeId, {
              id: traineeId,
              name: `${creneau.trainee.firstname} ${creneau.trainee.lastname}`,
              email: creneau.trainee.email || '',
              phone: creneau.trainee.phone || '',
              totalBookings: 0,
              lastBooking: creneau.creneau_date,
              status: 'active' as const,
              bookedCreneaux: []
            });
          }
          
          const client = clientMap.get(traineeId)!;
          client.totalBookings++;
          client.bookedCreneaux.push(creneau);
          
          if (new Date(creneau.creneau_date) > new Date(client.lastBooking)) {
            client.lastBooking = creneau.creneau_date;
          }
        }
      });
      
      setClients(Array.from(clientMap.values()));
      setCreneauToReject(null);
      setRejectionReason('');
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || t('consultant.clients.rejectError'));
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'potential': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return t('consultant.clients.active');
      case 'inactive': return t('consultant.clients.inactive');
      case 'potential': return t('consultant.clients.potential');
      default: return t('consultant.clients.unknown');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rejection Modal */}
      {showRejectModal && creneauToReject && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCancelReject}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t('consultant.clients.rejectBooking')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('consultant.clients.rejectReason')}
                </p>
              </div>
            </div>

            {/* Rejection Reason Input */}
            <div className="mb-6">
              <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.clients.rejectReason')} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('consultant.clients.rejectReasonPlaceholder')}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('consultant.clients.rejectReasonNote')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCancelReject}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <motion.button
                onClick={handleConfirmReject}
                disabled={!rejectionReason.trim() || actionLoading === creneauToReject}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: !rejectionReason.trim() || actionLoading === creneauToReject ? 1 : 1.02 }}
                whileTap={{ scale: !rejectionReason.trim() || actionLoading === creneauToReject ? 1 : 0.98 }}
              >
                {actionLoading === creneauToReject ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('consultant.clients.rejecting')}</span>
                  </div>
                ) : (
                  t('consultant.clients.confirmReject')
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('consultant.clients.title')}</h1>
          <p className="text-gray-600">{t('consultant.clients.subtitle')}</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          {errorMessage}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('consultant.clients.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">{t('consultant.clients.allStatuses')}</option>
              <option value="active">{t('consultant.clients.active')}</option>
              <option value="inactive">{t('consultant.clients.inactive')}</option>
              <option value="potential">{t('consultant.clients.potential')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredClients.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredClients.map((client) => (
              <div key={client.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
                        <p className="text-sm text-gray-600">{client.company}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="h-4 w-4" />
                            <span>{client.email}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Phone className="h-4 w-4" />
                            <span>{client.phone}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{client.totalBookings} {t('consultant.clients.reservations')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{t('consultant.clients.lastBooking')} {new Date(client.lastBooking).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                        {getStatusLabel(client.status)}
                      </span>
                    </div>
                    
                    {/* <div className="flex items-center gap-2">
                      <Link
                        to={`/consultant/clients/${client.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Voir le profil"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/consultant/clients/${client.id}/messages`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Envoyer un message"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Link>
                    </div> */}
                  </div>
                </div>
                
                {/* Upcoming Bookings */}
                {client.bookedCreneaux.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      {t('consultant.clients.reservations')}
                    </h4>
                    <div className="space-y-2">
                      {client.bookedCreneaux
                        .sort((a, b) => new Date(b.creneau_date).getTime() - new Date(a.creneau_date).getTime())
                        .map((creneau) => {
                          const isPending = creneau.booking_status === 'pending';
                          const isApproved = creneau.booking_status === 'approved';
                          const isRejected = creneau.booking_status === 'rejected';
                          const isPast = new Date(creneau.creneau_date) < new Date();
                          
                          return (
                            <div key={creneau.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                              isPending 
                                ? 'bg-yellow-50 border-yellow-200' 
                                : isRejected 
                                  ? 'bg-red-50 border-red-200'
                                  : isApproved 
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-gray-50 border-gray-200'
                            } ${isPast ? 'opacity-70' : ''}`}>
                              <div className="flex items-center gap-3 flex-1">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${
                                  isPending 
                                    ? 'bg-yellow-500' 
                                    : isRejected 
                                      ? 'bg-red-500'
                                      : isApproved 
                                        ? 'bg-green-500'
                                        : 'bg-gradient-to-r from-brand-primary to-purple-600'
                                }`}>
                                  <Calendar className="h-4 w-4 text-white" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900">{creneau.service.title}</p>
                                    {isPast && (
                                      <span className="text-xs text-gray-500 italic">({t('consultant.clients.past')})</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-600">
                                    <span>{new Date(creneau.creneau_date).toLocaleDateString('fr-FR')}</span>
                                    <span>{creneau.start_time} - {creneau.end_time}</span>
                                    {creneau.service.city && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {creneau.service.city}
                                      </span>
                                    )}
                                  </div>
                                  {isRejected && creneau.rejection_reason && (
                                    <p className="text-xs text-red-600 mt-1 italic">
                                      {t('consultant.clients.reason')} {creneau.rejection_reason}
                                    </p>
                                  )}
                                  {creneau.notes && (
                                    <p className="text-xs text-gray-500 mt-1 italic">
                                      {t('consultant.clients.note')} {creneau.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {isPending ? (
                                  <>
                                    <button
                                      onClick={() => handleApprove(creneau.id)}
                                      disabled={actionLoading === creneau.id || isPast}
                                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                      title={isPast ? t('consultant.clients.cannotApprovePast') : ''}
                                    >
                                      {actionLoading === creneau.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <>
                                          <CheckCircle2 className="h-3 w-3" />
                                          {t('consultant.clients.approve')}
                                        </>
                                      )}
                                    </button>
                                    <button
                                      onClick={() => handleRejectClick(creneau.id)}
                                      disabled={actionLoading === creneau.id}
                                      className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                      {actionLoading === creneau.id ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <>
                                          <XCircle className="h-3 w-3" />
                                          {t('consultant.clients.reject')}
                                        </>
                                      )}
                                    </button>
                                  </>
                                ) : (
                                  <span className={`px-2 py-1 text-xs font-medium rounded border ${
                                    isApproved 
                                      ? 'bg-green-100 text-green-700 border-green-200' 
                                      : isRejected
                                        ? 'bg-red-100 text-red-700 border-red-200'
                                        : 'bg-gray-100 text-gray-700 border-gray-200'
                                  }`}>
                                    {isApproved ? t('consultant.clients.confirmed') : isRejected ? t('consultant.clients.rejectedStatus') : t('consultant.clients.other')}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('consultant.clients.noClientsFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? t('consultant.clients.noClientsMatch')
                : t('consultant.clients.noReservations')
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <div className="mt-6">
                <p className="text-sm text-gray-500">
                  {t('consultant.clients.clientsWillAppear')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
